import ExcelJS from 'exceljs';
import { notification } from 'antd';
import { USER_ROLES, BANKS_NAMES, CARD_ACQUIRER_BANKS_NAMES, WALLET_NAMES, CHECKOUT_COM_AUTH_TYPE, TIMEZONE, BNPL_NAMES, RAAST_BANKS_NAMES, GOOGLE_PAY_BANKS_NAMES, StoreBlockList } from 'utils/constant';
import { getUserPermissions } from './storage';
import { CURRENCIES, SUB_CURRENCIES } from './currencies';
import { formatInTimeZone } from 'date-fns-tz';
import moment from 'moment-timezone';

function csvToRows(text) {
  return text
    ?.split(/\r?\n/)
    ?.filter(Boolean)
    ?.map((line) => {
      const row = [];
      let cur = '',
        inQ = false;
      for (let i = 0; i < line?.length; i++) {
        const c = line[i];
        if (c === '"') inQ = !inQ;
        else if (inQ) cur += c;
        else if (c === ',') {
          row?.push(cur?.trim());
          cur = '';
        } else cur += c;
      }
      row?.push(cur?.trim());
      return row;
    });
}

function worksheetToRows(worksheet) {
  const toStr = (v) => (v instanceof Date ? v?.toISOString()?.split('T')[0] : v != null ? String(v) : '');
  const headerRow = worksheet.getRow(1);
  const headers = [];
  headerRow.eachCell({ includeEmpty: true }, (cell) => headers.push(toStr(cell?.value)));
  const rows = [headers];
  for (let i = 2; i <= worksheet?.rowCount; i++) {
    const row = worksheet.getRow(i);
    const vals = [];
    for (let col = 1; col <= headers?.length; col++) vals?.push(toStr(row.getCell(col)?.value));
    rows?.push(vals);
  }
  return rows;
}

export const handleBinConfigByFileData = async (file) => {
  try {
    if (!file) return null;
    const dataArr = [];
    const headerValues = ['bin', 'discount_limit', 'discount_type', 'discount_value', 'rule_status', 'id', 'minimum_order_value', 'active_days', 'max_daily_usage', 'max_monthly_usage'];
    const undefineValues = [undefined, null, ''];
    const isCSV = file?.type === 'text/csv' || (file?.name && file?.name?.toLowerCase()?.endsWith('.csv'));

    const rows = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          if (isCSV) {
            resolve(csvToRows(e.target.result));
          } else {
            const workbook = new ExcelJS.Workbook();
            await workbook.xlsx.load(new Uint8Array(e.target.result));
            const ws = workbook.worksheets[0];
            resolve(ws ? worksheetToRows(ws) : []);
          }
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      isCSV ? reader.readAsText(file) : reader.readAsArrayBuffer(file);
    });

    if (!rows?.length) {
      notification.error({ message: 'Incomplete Data in File', placement: 'bottomRight' });
      return false;
    }
    const headers = isCSV ? rows[0]?.map((h) => String(h ?? '')?.trim()) : rows[0];
    const areAllValuesExist = headerValues?.every((val) => headers?.includes(val));
    if (!areAllValuesExist) {
      notification.error({ message: 'Incomplete Data in File', placement: 'bottomRight' });
      return false;
    }
    for (let i = 1; i < rows?.length; i++) {
      const obj = {};
      for (let j = 0; j < headers?.length; j++) {
        if (headerValues?.includes(headers[j]) && !undefineValues.includes(rows[i][j])) {
          obj[headers[j]] = rows[i][j];
        }
      }
      if (Object.keys(obj)?.length > 0) dataArr?.push(obj);
    }
    return dataArr;
  } catch (err) {
    console.log('error in file');
    throw err;
  }
};
export const getUsersRole = () => {
  const userAssignedRoles = getUserPermissions() || [];
  const isPlatformAccount = userAssignedRoles?.includes(USER_ROLES.PLATFORM_ACCOUNT);
  const isSuperAdmin = userAssignedRoles?.includes(USER_ROLES.XPAY_SUPER_ADMIN);
  const isCopilotAdmin = userAssignedRoles?.includes(USER_ROLES.XPAY_COPILOT_ADMIN);
  const isPermissionToAddLogo = userAssignedRoles?.includes(USER_ROLES.HBL_LOGO);
  const isMerchantAccount = userAssignedRoles?.includes(USER_ROLES.PLATFORM_ACCOUNT) ? false : true;
  const isPim = userAssignedRoles?.includes(USER_ROLES.XPAY_PIM);
  const isSubscriptionsModuleAccessOnly = userAssignedRoles?.includes(USER_ROLES.XPAY_SUBSCRIPTION_MODULE_ONLY);
  return { isPermissionToAddLogo, isPlatformAccount, isMerchantAccount, isSuperAdmin, isPim, isSubscriptionsModuleAccessOnly, isCopilotAdmin };
};
export const checkUserPermissions = (permissions) => {
  const userPermissions = getUserPermissions() || [];
  let isPermissionsExist = false;
  if (userPermissions && permissions) {
    isPermissionsExist = userPermissions.some((code) => {
      if ((code === USER_ROLES.XPAY_ADMIN || code === USER_ROLES.XPAY_SUPER_ADMIN || code === USER_ROLES.XPAY_BRAND_ADMIN) && permissions?.includes(code)) {
        // checking here if above three role assign then these role should not be in permission list
        const isRoleExist =
          userPermissions?.includes(USER_ROLES.XPAY_DEVELOPER) ||
          userPermissions?.includes(USER_ROLES.XPAY_RESTRICTED_ADMIN) ||
          userPermissions?.includes(USER_ROLES.XPAY_USER) ||
          userPermissions?.includes(USER_ROLES.XPAY_REFUND) ||
          userPermissions?.includes(USER_ROLES.XPAY_SUBSCRIPTION_USER);
        return isRoleExist ? false : true;
      } else {
        return permissions?.includes(code);
      }
    });
  }
  return isPermissionsExist;
};

export const searchBank = (key) => {
  if (BANKS_NAMES.hasOwnProperty(key)) {
    return BANKS_NAMES[key];
  } else {
    return key;
  }
};
export const BanksName = Object?.entries(BANKS_NAMES).map(([key, value]) => {
  return { label: value, value: key };
});
export const CardAcquirerBanksName = Object?.entries(CARD_ACQUIRER_BANKS_NAMES).map(([key, value]) => {
  return { label: value, value: key };
});
export const WalletsName = Object?.entries(WALLET_NAMES).map(([key, value]) => {
  return { label: value, value: key };
});

export const BnplName = Object?.entries(BNPL_NAMES).map(([key, value]) => {
  return { label: value, value: key };
});
export const RaastBanksName = Object?.entries(RAAST_BANKS_NAMES).map(([key, value]) => {
  return { label: value, value: key };
});
export const GooglePayBanksName = Object?.entries(GOOGLE_PAY_BANKS_NAMES).map(([key, value]) => {
  return { label: value, value: key };
});
export const AuthName = Object?.entries(CHECKOUT_COM_AUTH_TYPE).map(([key, value]) => {
  return { label: value, value: key };
});

export const TimezoneNames = Object?.entries(TIMEZONE).map(([key, value]) => {
  return { label: value, value: key };
});
export const CurrencyNames = Object.keys(CURRENCIES).map((key) => ({
  label: CURRENCIES[key]?.code,
  value: CURRENCIES[key]?.code,
}));

export const SUB_CurrencyNames = Object.keys(SUB_CURRENCIES).map((key) => ({
  label: SUB_CURRENCIES[key]?.code,
  value: SUB_CURRENCIES[key]?.code,
}));

export const StoreBlockListOptions = Object.entries(StoreBlockList).map(([key, value]) => ({
  label: value,
  value: key,
}));

export const isoToFormattedDateString = (isoDateString) => {
  const date = new Date(isoDateString);
  const timezoneOffset = date?.getTimezoneOffset() * 60000;
  const localDate = new Date(date?.getTime() + timezoneOffset);
  const options = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: true,
  };
  const formattedDateString = localDate?.toLocaleString('en-US', options);
  return formattedDateString;
};

export const convertToStoreTimezone = async (result, timezone) => {
  const eventsWithLocalTime = result?.map((event) => {
    const createKey = event.createdAt ? 'createdAt' : 'created_at';
    const updateKey = event.updatedAt ? 'updatedAt' : 'last_update';
    if (event[createKey]) {
      const localCreatedAt = formatInTimeZone(event[createKey], timezone, 'MMM d, yyyy h:mm:ss a');
      event[createKey] = localCreatedAt;
    }
    if (event[updateKey]) {
      const localUpdatedAt = formatInTimeZone(event[updateKey], timezone, 'MMM d, yyyy h:mm:ss a');
      event[updateKey] = localUpdatedAt;
    }
    return event;
  });
  return eventsWithLocalTime;
};
export const convertToSubscriptionTimezone = async (result, timezone, currentArrayName) => {
  const products = result?.[currentArrayName]?.map((event) => {
    const createKey = event?.createdAt ? 'createdAt' : 'created_at';
    const updateKey = event?.updatedAt ? 'updatedAt' : 'last_update';
    const dateKey = event?.date ? 'date' : 'created_at';
    if (event[createKey]) {
      const localCreatedAt = formatInTimeZone(event[createKey], timezone, 'MMM d, yyyy h:mm:ss a');
      event[createKey] = localCreatedAt;
    }
    if (event[updateKey]) {
      const localUpdatedAt = formatInTimeZone(event[updateKey], timezone, 'MMM d, yyyy h:mm:ss a');
      event[updateKey] = localUpdatedAt;
    }
    if (event[dateKey]) {
      const localDate = formatInTimeZone(event[dateKey], timezone, 'MMM d, yyyy h:mm:ss a');
      event[dateKey] = localDate;
    }
    return event;
  });

  const eventsWithLocalTime = {
    count: result?.count,
    products: products,
  };
  return eventsWithLocalTime;
};

export const formatAmount = (amount, currency = 'PKR') => {
  const fractionDigits = currency === 'OMR' ? 3 : 2;
  return Number(amount || 0).toLocaleString('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
};

export const checkIsArraysAreSame = (arr1, arr2) => {
  if (arr1?.length !== arr2?.length) {
    return false;
  }
  let sortedArr1 = Array.from(arr1)?.sort();
  let sortedArr2 = Array.from(arr2)?.sort();
  for (let i = 0; i < sortedArr1?.length; i++) {
    if (sortedArr1[i] !== sortedArr2[i]) {
      return false;
    }
  }
  return true;
};
const getFormattedDateTime = (date, timeZone, milliseconds = 0) => {
  if (timeZone) {
    return moment.tz(date?.format('YYYY-MM-DDTHH:mm:ss.SSS'), timeZone)?.utc()?.milliseconds(milliseconds)?.toISOString();
  }
  return new Date(date)?.toISOString();
};
export const handleDateRangeFormat = (dateRange, targetTimeZone = null) => {
  if (!dateRange || dateRange?.length !== 2) return;
  const start_date = getFormattedDateTime(dateRange[0], targetTimeZone);
  const end_date = getFormattedDateTime(dateRange[1], targetTimeZone, 999);
  return { start_date, end_date };
};

export const handleStateByPassData = (data, parseData = false) => {
  if (!data) return parseData ? null : '';
  try {
    if (parseData) {
      const parsedData = JSON?.parse(data);
      if (parsedData?.filtersData?.date_range?.length) {
        parsedData.filtersData.date_range[0] = moment(parsedData?.filtersData?.date_range[0]);
        parsedData.filtersData.date_range[1] = moment(parsedData?.filtersData?.date_range[1]);
      }
      return parsedData;
    } else {
      if (data?.filtersData?.date_range?.length) {
        data.filtersData.date_range[0] = data?.filtersData?.date_range[0]?.toISOString();
        data.filtersData.date_range[1] = data?.filtersData?.date_range[1]?.toISOString();
      }
      return JSON?.stringify(data);
    }
  } catch (error) {
    console.error('Error', error);
    return parseData ? null : '';
  }
};

export function getFormattedDateForApiLogs(data) {
  const start_date_raw = new Date(data?.createdAt || data?.created_at);
  const end_date_raw = new Date(data?.updatedAt || data?.last_update);
  if (isNaN(start_date_raw) || isNaN(end_date_raw)) {
    throw new Error('Invalid date input');
  }
  start_date_raw?.setMinutes(start_date_raw?.getMinutes() - 1);
  end_date_raw?.setMinutes(end_date_raw?.getMinutes() + 1);
  const formatDate = (date) => date?.toISOString()?.split('.')[0];
  return {
    start_date: formatDate(start_date_raw),
    end_date: formatDate(end_date_raw),
  };
}

export function getXAxisLabel(dateRange) {
  if (dateRange && dateRange?.start_date && dateRange?.end_date) {
    const start = new Date(dateRange?.start_date);
    const end = new Date(dateRange?.end_date);

    // difference in days
    const diffInDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));

    // if (diffInDays > 31 && diffInDays <= 180) {
    //   return { label: 'Week', value: 'week' };
    // } else
    if (diffInDays > 31) {
      return { label: 'Month', value: 'month' };
    } else {
      return { label: 'Day', value: 'day' };
    }
  } else {
    return { label: 'Month', value: 'month' };
  }
}
